import { api } from '../../lib/api.js';

/** Where a return was rung up — GET /returns works it out (returns.service.ts). */
export type ReturnSource = 'till' | 'register' | 'office';

export interface ReturnRefund { id: string; number: string; amount: string; currencyCode: string }
export interface ReturnRestock { warehouseId: string; warehouseName: string | null; units: string }
export interface ReturnItemBrief { code: string | null; name: string | null; quantity: string; unitName: string | null }

/** One row of GET /returns: a credit note plus what it does not say about itself. */
export interface ReturnRow {
  id: string;
  docNumber: string;
  docDate?: string;
  createdAt?: string;
  status: string;
  currencyCode: string;
  total?: string;
  notes?: string | null;
  partyId?: string | null;
  partyName?: string | null;
  partyCode?: string | null;
  createdByName?: string | null;
  warehouseId?: string | null;
  warehouseName?: string | null;
  creditOfInvoiceId?: string | null;
  originalNumber?: string | null;
  originalDate?: string | null;
  originalTotal?: string | null;
  lineCount?: number;
  unitCount?: number;
  source?: ReturnSource;
  refund?: ReturnRefund | null;
  restock?: ReturnRestock | null;
  items?: ReturnItemBrief[];
  moreItems?: number;
}

export interface ReturnLine {
  lineNo: number;
  itemId?: string | null;
  itemCode?: string | null;
  itemName?: string | null;
  description?: string | null;
  unitName?: string | null;
  quantity: string;
  unitPrice: string;
  taxAmount?: string | null;
  lineTotal?: string | null;
}

/** GET /returns/:id — the row's facts plus the lines and the money breakdown. */
export interface ReturnDetail extends ReturnRow {
  subtotal?: string;
  taxTotal?: string;
  discountTotal?: string;
  partyPhone?: string | null;
  lines: ReturnLine[];
}

/** Every return in reach, a page at a time — the list holds them all. */
export async function fetchAllReturns(): Promise<ReturnRow[]> {
  const PAGE = 500;
  const all: ReturnRow[] = [];
  for (let offset = 0; offset < 50_000; offset += PAGE) {
    const r = await api().get<{ items: ReturnRow[]; total: number }>('/returns', { params: { limit: PAGE, offset } });
    all.push(...r.data.items);
    if (r.data.items.length < PAGE || all.length >= r.data.total) break;
  }
  return all;
}

/** Sum an amount per currency — returns are never added across currencies. */
export function sumByCurrency<T>(rows: readonly T[], pick: (r: T) => { amount: string | number | null | undefined; currency: string | null | undefined } | null): Array<{ currency: string; amount: number }> {
  const m = new Map<string, number>();
  for (const r of rows) {
    const v = pick(r);
    if (!v?.currency) continue;
    m.set(v.currency, (m.get(v.currency) ?? 0) + Number(v.amount ?? 0));
  }
  return [...m.entries()].map(([currency, amount]) => ({ currency, amount })).sort((a, b) => b.amount - a.amount);
}
